import { Layer, SelectableTile } from "@carbon/react";
import { FC } from "react";
import { Controller, FormProvider, useFormState } from "react-hook-form";

import { useT } from "src/internationalization";
import { AlertValidationDecision, AlertValidationDecisionValues } from "src/types/alert";
import { cn } from "src/utils/string.utils.ts";

import { CaseAlertsHistory } from "./CaseAlertsHistory";
import { DecisionFormControl } from "./useDecisionForm";

const DECISION_LABELS: { [key in AlertValidationDecision] } = {
  compliant: {
    title: "alerts.confirmFraud.decision.compliant.title",
    description: "alerts.confirmFraud.decision.compliant.description",
  },
  fraudPrevented: {
    title: "alerts.confirmFraud.decision.fraudPrevented.title",
    description: "alerts.confirmFraud.decision.fraudPrevented.description",
  },
  provenFraudWithFinancialLoss: {
    title: "alerts.confirmFraud.decision.proven_fraud_with_financial_loss.title",
    description: "alerts.confirmFraud.decision.proven_fraud_with_financial_loss.description",
  },
  provenFraudWithNoFinancialLoss: {
    title: "alerts.confirmFraud.decision.provedFraudWithNoFinancialLoss.title",
    description: "alerts.confirmFraud.decision.provedFraudWithNoFinancialLoss.description",
  },
};

export type DecisionFormProps = {
  decisionFormControl: DecisionFormControl;
  onDecisionChange: (decision: AlertValidationDecision) => void;
  caseId: string;
  caseNumber: string;
};

export const DecisionForm: FC<DecisionFormProps> = ({
  decisionFormControl,
  caseId,
  caseNumber,
  onDecisionChange,
}) => {
  const t = useT();
  const { errors } = useFormState({ control: decisionFormControl.control });

  return (
    <Layer className="h-full flex flex-col">
      <FormProvider {...decisionFormControl.methods}>
        <Controller
          name="decision"
          control={decisionFormControl.control}
          rules={{ required: true }}
          render={({ field }) => (
            <div className={"mb-5"}>
              <div className="flex flex-row gap-4 mb-5">
                {AlertValidationDecisionValues.map((value) => {
                  const labels = DECISION_LABELS[value];

                  return (
                    <SelectableTile
                      key={value}
                      value={value}
                      title={t(labels.title)}
                      data-decision-input
                      selected={field.value === value}
                      onChange={() => {
                        field.onChange(field.value === value ? null : value);
                        onDecisionChange(value);
                      }}
                      className={cn(
                        "flex-1 min-w-0 cursor-pointer",
                        errors.decision &&
                          "!outline-supportError border-none outline -outline-offset-2 outline-2"
                      )}
                      tabIndex={0}
                      aria-pressed={field.value === value}
                    >
                      <div className="font-normal text-lg mb-2">{t(labels.title)}</div>
                      <div className="text-gray-600 text-sm">{t(labels.description)}</div>
                    </SelectableTile>
                  );
                })}
              </div>
              {errors.decision && (
                <span className="text-textError flex gap-2 items-center">
                  {t("general.form.field.required")}
                </span>
              )}
            </div>
          )}
        />
      </FormProvider>
      <CaseAlertsHistory caseId={caseId} caseNumber={caseNumber} />
    </Layer>
  );
};
